// BasicInfoStep Component
// Screen 2: Collect basic personal information (REQUIRED)

import React, { useState } from 'react';
import { FaUser, FaMapMarkerAlt, FaMars, FaVenus, FaGenderless, FaUserSecret, FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import LocationPicker from '../../LocationPicker/LocationPicker';
import styles from './BasicInfoStep.module.css';

const BasicInfoStep = ({ formData, updateFormData, onNext, onBack }) => {
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const handleChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const handleLocationChange = (field, value) => {
    updateFormData({
      location: {
        ...formData.location,
        [field]: value
      }
    });
  };

  const handleLocationSelect = (locationData) => {
    updateFormData({ location: locationData });
    setShowLocationPicker(false);
  };

  const isFormValid = () => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.dateOfBirth !== '' &&
      formData.gender !== '' &&
      formData.location.city !== '' &&
      formData.location.country !== ''
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Basic Information</h2>
        <p className={styles.subtitle}>Let's start with the essentials</p>
      </div>

      <div className={styles.content}>
        {/* Email (Read-only, from Firebase) */}
        {formData.email && (
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaEnvelope /> Email
            </label>
            <div className={styles.emailDisplay}>
              <input
                type="email"
                className={`${styles.input} ${styles.readOnly}`}
                value={formData.email}
                readOnly
                disabled
              />
              <FaCheckCircle className={styles.verifiedIcon} title="Verified" />
            </div>
          </div>
        )}

        {/* Name Fields */}
        <div className={styles.nameGrid}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              First Name <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              className={styles.input}
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              placeholder="First name"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Last Name <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              className={styles.input}
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              placeholder="Last name"
            />
          </div>
        </div>

        {/* Date of Birth */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Date of Birth <span className={styles.required}>*</span>
          </label>
          <input
            type="date"
            className={styles.input}
            value={formData.dateOfBirth}
            onChange={(e) => handleChange('dateOfBirth', e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* Gender */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Gender <span className={styles.required}>*</span>
          </label>
          <div className={styles.genderButtons}>
            <button
              type="button"
              className={`${styles.genderButton} ${formData.gender === 'male' ? styles.active : ''}`}
              onClick={() => handleChange('gender', 'male')}
            >
              <FaMars className={styles.genderIcon} />
              <span>Male</span>
            </button>
            <button
              type="button"
              className={`${styles.genderButton} ${formData.gender === 'female' ? styles.active : ''}`}
              onClick={() => handleChange('gender', 'female')}
            >
              <FaVenus className={styles.genderIcon} />
              <span>Female</span>
            </button>
            <button
              type="button"
              className={`${styles.genderButton} ${formData.gender === 'other' ? styles.active : ''}`}
              onClick={() => handleChange('gender', 'other')}
            >
              <FaGenderless className={styles.genderIcon} />
              <span>Other</span>
            </button>
            <button
              type="button"
              className={`${styles.genderButton} ${formData.gender === 'prefer_not_to_say' ? styles.active : ''}`}
              onClick={() => handleChange('gender', 'prefer_not_to_say')}
            >
              <FaUserSecret className={styles.genderIcon} />
              <span>Prefer not to say</span>
            </button>
          </div>
        </div>

        {/* Location */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            <FaMapMarkerAlt /> Location <span className={styles.required}>*</span>
          </label>
          
          <div className={styles.locationGrid}>
            <input
              type="text"
              className={styles.input}
              value={formData.location.city || ''}
              onChange={(e) => handleLocationChange('city', e.target.value)}
              placeholder="City *"
            />
            <input
              type="text"
              className={styles.input}
              value={formData.location.state || ''}
              onChange={(e) => handleLocationChange('state', e.target.value)}
              placeholder="State/Province"
            />
            <input
              type="text"
              className={styles.input}
              value={formData.location.country || ''}
              onChange={(e) => handleLocationChange('country', e.target.value)}
              placeholder="Country *"
            />
          </div>
          
          {/* Map Picker Button */}
          {process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
            <button
              type="button"
              className={styles.pickLocationButton}
              onClick={() => setShowLocationPicker(true)}
            >
              <FaMapMarkerAlt />
              {formData.location?.city ? 'Change Location on Map' : 'Pick Location from Map'}
            </button>
          )}
        </div>
        
        {/* Location Picker Modal */}
        {showLocationPicker && (
          <div className={styles.modalOverlay} onClick={() => setShowLocationPicker(false)}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3>
                  <FaMapMarkerAlt />
                  Pick Location
                </h3>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={() => setShowLocationPicker(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className={styles.modalBody}>
                <LocationPicker
                  value={formData.location}
                  onLocationSelect={handleLocationSelect}
                />
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className={styles.buttons}>
          <button
            className={styles.backButton}
            onClick={onBack}
          >
            Back
          </button>
          <button
            className={styles.nextButton}
            onClick={onNext}
            disabled={!isFormValid()}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoStep;

